import {
  CalendarIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
  DocumentCheckIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  UserIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import AdminApiService from '../../utils/api';
import PhoneActions from '../../components/PhoneActions';

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-success-100 text-success-800 border-success-200',
    inactive: 'bg-gray-100 text-gray-800 border-gray-200',
    suspended: 'bg-danger-100 text-danger-800 border-danger-200'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const VerificationBadge = ({ status }) => {
  const styles = {
    verified: 'bg-success-100 text-success-800',
    pending: 'bg-warning-100 text-warning-800',
    rejected: 'bg-danger-100 text-danger-800'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {status === 'verified' && <CheckCircleIcon className="w-3 h-3 mr-1" />}
      <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
    </span>
  );
};

const BuyerCard = ({ buyer, onAction }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4, scale: 1.02 }}
    className="bg-white rounded-xl p-6 shadow-soft border border-gray-200 hover:shadow-lg transition-all duration-300"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center space-x-4">
        <div className="relative">
          <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
            <UserIcon className="w-8 h-8 text-primary-600" />
          </div>
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
            buyer.status === 'active' ? 'bg-success-500' : 'bg-gray-400'
          }`}></div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{buyer.name}</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
            <MapPinIcon className="w-4 h-4" />
            <span>{buyer.location}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
            <CalendarIcon className="w-4 h-4" />
            <span>Joined {new Date(buyer.registrationDate).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end space-y-2">
        <StatusBadge status={buyer.status} />
        <VerificationBadge status={buyer.verificationStatus} />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="text-center p-3 bg-primary-50 rounded-lg">
        <div className="text-2xl font-bold text-primary-600">{buyer.totalPurchases}</div>
        <div className="text-xs text-gray-600">Purchases</div>
      </div>
      <div className="text-center p-3 bg-success-50 rounded-lg">
        <div className="text-2xl font-bold text-success-600">₹{(buyer.totalSpent / 100000).toFixed(1)}L</div>
        <div className="text-xs text-gray-600">Total Spent</div>
      </div>
      <div className="text-center p-3 bg-secondary-50 rounded-lg">
        <div className="text-2xl font-bold text-secondary-600">{buyer.wishlistItems}</div>
        <div className="text-xs text-gray-600">Wishlist</div>
      </div>
      <div className="text-center p-3 bg-warning-50 rounded-lg">
        <div className="text-2xl font-bold text-warning-600">{buyer.profileCompletion}%</div>
        <div className="text-xs text-gray-600">Profile</div>
      </div>
    </div>

    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
      <div className="text-sm font-medium text-gray-900 mb-2">Buyer Information</div>
      <div className="grid grid-cols-1 gap-1 text-sm text-gray-700">
        <div><span className="font-medium text-gray-900">ID:</span> {buyer._id || buyer.id || 'N/A'}</div>
        <div><span className="font-medium text-gray-900">Email:</span> {buyer.email || 'N/A'}</div>
        <div className="flex items-center gap-1.5"><span className="font-medium text-gray-900">Phone:</span> <PhoneActions phone={buyer.phone} /></div>
        <div><span className="font-medium text-gray-900">Role:</span> {buyer.role || 'buyer'}</div>
        <div><span className="font-medium text-gray-900">Membership:</span> {buyer.membershipType || 'basic'}</div>
        <div><span className="font-medium text-gray-900">Favorites:</span> {buyer.favoritesCount}</div>
        <div><span className="font-medium text-gray-900">Test Drives:</span> {buyer.testDriveCount}</div>
        <div><span className="font-medium text-gray-900">Login Count:</span> {buyer.loginCount || 0}</div>
        <div><span className="font-medium text-gray-900">Email Verified:</span> {buyer.isEmailVerified ? 'Yes' : 'No'}</div>
        <div><span className="font-medium text-gray-900">Phone Verified:</span> {buyer.isPhoneVerified ? 'Yes' : 'No'}</div>
        <div><span className="font-medium text-gray-900">Address:</span> {buyer.fullAddress || 'Not specified'}</div>
      </div>
    </div>

    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
      <div className="flex items-center space-x-2">
        <PhoneActions phone={buyer.phone} textClassName="text-gray-600 font-normal" />
      </div>
      <div className="flex items-center space-x-2">
        <Link
          to={`/buyers/${buyer._id || buyer.id}`}
          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        >
          <EyeIcon className="w-4 h-4" />
        </Link>
        <button
          onClick={() => onAction(buyer.status === 'active' ? 'suspend' : 'activate', buyer)}
          className={`p-2 rounded-lg transition-colors ${
            buyer.status === 'active'
              ? 'text-gray-400 hover:text-danger-600 hover:bg-danger-50'
              : 'text-gray-400 hover:text-success-600 hover:bg-success-50'
          }`}
        >
          {buyer.status === 'active' ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
        </button>
      </div>
    </div>
  </motion.div>
);

const BuyersManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBuyers();
  }, []);

  const fetchBuyers = async () => {
    setLoading(true);
    try {
      const response = await AdminApiService.getBuyers();
      const buyersData = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.buyers)
            ? response.data.buyers
            : [];
      
      // Map backend data to expected frontend format
      const formattedBuyers = (Array.isArray(buyersData) ? buyersData : []).map(b => ({
        ...b,
        name: `${b.firstName || ''} ${b.lastName || ''}`.trim() || 'Unknown User',
        location: (() => {
          const address = b.address || b.location;
          if (typeof address === 'object' && address) {
            return `${address.city || ''}, ${address.state || ''}`.replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Not specified';
          }
          return address || 'Not specified';
        })(),
        registrationDate: b.createdAt || new Date(),
        status: b.status || (b.isActive === false ? 'inactive' : 'active'),
        verificationStatus: b.isVerified ? 'verified' : 'pending',
        totalPurchases: b.totalPurchases || 0,
        totalSpent: b.totalSpent || 0,
        wishlistItems: b.favorites?.length || b.savedCars?.length || 0,
        favoritesCount: b.favorites?.length || 0,
        testDriveCount: b.testDrives?.length || 0,
        membershipType: (typeof b.membership === 'object' ? b.membership?.type : b.membership) || 'basic',
        fullAddress: [
          b.address?.street,
          b.address?.city,
          b.address?.state,
          b.address?.pincode,
          b.address?.country
        ].filter(Boolean).join(', '),
        profileCompletion: b.firstName && b.lastName && b.email && b.phone ? 100 : 50,
        activity: {
          searches: b.activity?.searches || 0,
          views: b.activity?.views || 0,
          inquiries: b.activity?.inquiries || 0,
          testDrives: b.activity?.testDrives || 0
        },
        preferences: {
          budget: {
            min: b.preferences?.budget?.min || 0,
            max: b.preferences?.budget?.max || 0
          },
          brands: Array.isArray(b.preferences?.brands) ? b.preferences.brands : [],
          fuelType: Array.isArray(b.preferences?.fuelType) ? b.preferences.fuelType : []
        }
      }));
      
      setBuyers(formattedBuyers);
    } catch (error) {
      console.error('Failed to fetch buyers:', error);
      toast.error('Failed to load buyers data');
      setBuyers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedBuyers = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    const hasSearch = search.length > 0;
    const hasStatusFilter = statusFilter !== 'all';
    const hasVerificationFilter = verificationFilter !== 'all';
    const hasAnyFilter = hasSearch || hasStatusFilter || hasVerificationFilter;

    const source = hasAnyFilter
      ? buyers.filter((buyer) => {
          const buyerName = (buyer.name || '').toLowerCase();
          const buyerEmail = (buyer.email || '').toLowerCase();
          const buyerLocation = (buyer.location || '').toLowerCase();

          const matchesSearch = !hasSearch ||
            buyerName.includes(search) ||
            buyerEmail.includes(search) ||
            buyerLocation.includes(search);
          const matchesStatus = !hasStatusFilter || buyer.status === statusFilter;
          const matchesVerification = !hasVerificationFilter || buyer.verificationStatus === verificationFilter;

          return matchesSearch && matchesStatus && matchesVerification;
        })
      : [...buyers];

    return source.sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'spent':
            return b.totalSpent - a.totalSpent;
          case 'purchases':
            return b.totalPurchases - a.totalPurchases;
          case 'registrationDate':
            return new Date(b.registrationDate) - new Date(a.registrationDate);
          default:
            return 0;
        }
      });
  }, [buyers, searchTerm, statusFilter, verificationFilter, sortBy]);

  const handleAction = async (action, buyer) => {
    const buyerId = buyer._id || buyer.id;
    try {
      switch (action) {
        case 'activate':
          await AdminApiService.unblockBuyer(buyerId);
          toast.success(`${buyer.name} has been activated`);
          fetchBuyers();
          break;
        case 'suspend':
          await AdminApiService.blockBuyer(buyerId);
          toast.error(`${buyer.name} has been suspended`);
          fetchBuyers();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Failed to ${action} buyer:`, error);
      toast.error(`Failed to ${action} buyer`);
    }
  };

  const stats = {
    total: buyers.length,
    active: buyers.filter(b => b.status === 'active').length,
    verified: buyers.filter(b => b.verificationStatus === 'verified').length,
    totalSpent: buyers.reduce((sum, b) => sum + b.totalSpent, 0),
    totalPurchases: buyers.reduce((sum, b) => sum + b.totalPurchases, 0)
  };

  const activityData = [
    { name: 'Searches', value: buyers.reduce((sum, b) => sum + b.activity.searches, 0) },
    { name: 'Views', value: buyers.reduce((sum, b) => sum + b.activity.views, 0) },
    { name: 'Inquiries', value: buyers.reduce((sum, b) => sum + b.activity.inquiries, 0) },
    { name: 'Test Drives', value: buyers.reduce((sum, b) => sum + (b.activity?.testDrives || 0), 0) }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <h1 className="text-3xl font-display font-bold text-gray-900">
            Buyers Management
          </h1>
          <p className="text-gray-600 mt-2">
            Monitor buyer activity, purchase history, and engagement metrics
          </p>
        </motion.div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Buyers</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="p-4 bg-primary-100 rounded-xl">
              <UserGroupIcon className="w-8 h-8 text-primary-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Buyers</p>
              <p className="text-3xl font-bold text-success-600 mt-2">{stats.active}</p>
            </div>
            <div className="p-4 bg-success-100 rounded-xl">
              <CheckCircleIcon className="w-8 h-8 text-success-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Verified</p>
              <p className="text-3xl font-bold text-warning-600 mt-2">{stats.verified}</p>
            </div>
            <div className="p-4 bg-warning-100 rounded-xl">
              <DocumentCheckIcon className="w-8 h-8 text-warning-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Purchases</p>
              <p className="text-3xl font-bold text-secondary-600 mt-2">{stats.totalPurchases}</p>
            </div>
            <div className="p-4 bg-secondary-100 rounded-xl">
              <ShoppingCartIcon className="w-8 h-8 text-secondary-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Spent</p>
              <p className="text-3xl font-bold text-green-600 mt-2">₹{(stats.totalSpent / 10000000).toFixed(1)}Cr</p>
            </div>
            <div className="p-4 bg-green-100 rounded-xl">
              <CurrencyRupeeIcon className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Activity Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Buyer Activity Overview</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={activityData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="name" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip />
            <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search buyers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-64"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            
            <select
              value={verificationFilter}
              onChange={(e) => setVerificationFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Verification</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="name">Sort by Name</option>
              <option value="spent">Sort by Amount Spent</option>
              <option value="purchases">Sort by Purchases</option>
              <option value="registrationDate">Sort by Registration Date</option>
            </select>
            <span className="text-sm text-gray-500">
              {filteredAndSortedBuyers.length} of {buyers.length} buyers
            </span>
          </div>
        </div>
      </motion.div>

      {/* Buyers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredAndSortedBuyers.map((buyer, index) => (
          <motion.div
            key={buyer._id || buyer.id || buyer.email}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <BuyerCard buyer={buyer} onAction={handleAction} />
          </motion.div>
        ))}
      </div>

      {filteredAndSortedBuyers.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <UserGroupIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No buyers found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </motion.div>
      )}
    </div>
  );
};

export default BuyersManagement;